(ns app.domain.letter.templates-config
  (:require [app.domain.application.condition.model :as condition-model]
            [app.domain.group.permit-type-config.model :as ptc-model]))

(def letter-pdf-template-types
  [:letter-pdf-template.type/workspace
   :letter-pdf-template.type/licence
   :letter-pdf-template.type/business])

(defn letter-pdf-template-type-choice [permit-type]
  (case permit-type
    :permit-type/L letter-pdf-template-types
    (:permit-type/P
      :permit-type/CE) [:letter-pdf-template.type/workspace]
    [:letter-pdf-template.type/workspace
     :letter-pdf-template.type/business]))

;; Letter fragment applicability

(defn- violation-snippet-applicable? [{:keys [template-type group-features]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (contains? group-features :group.feature/inspections)))

(defn- inspection-or-violation-snippets-applicable? [{:keys [template-type group-features]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (contains? group-features :group.feature/inspections)))

(defn- bill-snippets-applicable? [{:keys [template-type group-features]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (contains? group-features :group.feature/fees-and-payments)))

(defn- party-snippet-applicable? [{:keys [template-type permit-type]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (not= :permit-type/CE permit-type)))

(defn- bonds-applicable? [{:keys [template-type group-features]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (contains? group-features :group.feature/use-bond-accounts)))

(defn- conditions-applicable? [{:keys [template-type group feature-flags]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (condition-model/template-snippets-enabled? group feature-flags)))

(defmulti letter-fragment-applicable?
  "Given a letter fragment type and a context of permit-type, template-type,
          group-features, and feature-flags, returns whether the snippet for the
          fragment type should be visible.

          Note that it is enough to provide proper implementation for the \"top level\"
          fragments only, nested fragments are hidden whenever their parents are."
  (fn [letter-fragment-type _ctx]
    letter-fragment-type))

(defmethod letter-fragment-applicable? :default [_ _]
  true)

(defmethod letter-fragment-applicable? :letter-fragment/property-owner-snippet [_ {:keys [permit-type template-type]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (contains? ptc-model/permit-types-with-properties permit-type)))

(defmethod letter-fragment-applicable? :letter-fragment/applicant-snippet [_ ctx]
  (party-snippet-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/party-snippet [_ ctx]
  (party-snippet-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/violation-snippet [_ ctx]
  (violation-snippet-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/non-compliant-violation-snippet [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/compliant-violation-snippet [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/paid-bill-snippet [_ ctx]
  (bill-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/unpaid-bill-snippet [_ ctx]
  (bill-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/no-access-inspection-snippet [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/not-complete-inspection-snippet [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/failed-inspection-snippet [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/passed-inspection-snippet [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/bond-snippet [_ ctx]
  (bonds-applicable? ctx))

(defmethod letter-fragment-applicable? :letter-fragment/condition-snippet [_ ctx]
  (conditions-applicable? ctx))

;; Workspace template variable applicability

(defn- licensing? [permit-type]
  (contains? #{:permit-type/L :permit-type/LS} permit-type))

(defn- permit-type-has-properties? [permit-type]
  (contains? ptc-model/permit-types-with-properties permit-type))

(defn- reviews-applicable? [{:keys [permit-type template-type]}]
  (and (= template-type :letter-pdf-template.type/workspace)
       (contains? ptc-model/permit-types-with-reviews permit-type)))

(defn- applicants-applicable? [{:keys [permit-type]}]
  (not= permit-type :permit-type/CE))

(defn- deficiencies-applicable? [{:keys [permit-type group-features]}]
  (and (not= permit-type :permit-type/CE)
       (contains? group-features :group.feature/inspections)))

(defn- violations-applicable? [{:keys [permit-type group-features]}]
  (and (= permit-type :permit-type/CE)
       (contains? group-features :group.feature/inspections)))

(defn- compliant-violations-applicable? [{:keys [permit-type group-features]}]
  (and (= permit-type :permit-type/CE)
       (contains? group-features :group.feature/inspections)))

(defn- holder-applicable? [{:keys [permit-type]}]
  (or (licensing? permit-type)
      (= permit-type :permit-type/CR)))

(defn- case-and-legal-description-applicable? [{:keys [permit-type]}]
  (= permit-type :permit-type/CE))

(defmulti workspace-letter-variable-applicable?
  "Given a workspace template variable and a context of permit-type, template-type,
          group-features, and feature-flags, returns whether the template variable is applicable."
  (fn [template-variable _ctx]
    template-variable))

(defmethod workspace-letter-variable-applicable? :default [_ _]
  true)

(defmethod workspace-letter-variable-applicable? "application-number" [_ {:keys [permit-type]}]
  (and (not= permit-type :permit-type/CE)
       (not (licensing? permit-type))))

(defmethod workspace-letter-variable-applicable? "case-number" [_ {:keys [permit-type]}]
  (= permit-type :permit-type/CE))

(defmethod workspace-letter-variable-applicable? "licence-number" [_ {:keys [permit-type]}]
  (licensing? permit-type))

(defmethod workspace-letter-variable-applicable? "category" [_ {:keys [permit-type]}]
  (and (not= permit-type :permit-type/CR)
       (not (licensing? permit-type))))

(defmethod workspace-letter-variable-applicable? "additional-categories" [_ {:keys [permit-type feature-flags]}]
  (and (= permit-type :permit-type/CE)
       (contains? feature-flags :feature-flag.id/allow-multiple-categories-to-a-case)))

(defmethod workspace-letter-variable-applicable? "licence-type" [_ {:keys [permit-type]}]
  (licensing? permit-type))

(defmethod workspace-letter-variable-applicable? "registration-type" [_ {:keys [permit-type]}]
  (= permit-type :permit-type/CR))

(defmethod workspace-letter-variable-applicable? "trades" [_ {:keys [permit-type]}]
  (= permit-type :permit-type/CR))

(defmethod workspace-letter-variable-applicable? "unpaid-bills" [_ ctx]
  (bill-snippets-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "paid-bills" [_ ctx]
  (bill-snippets-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "no-access-inspections" [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "not-complete-inspections" [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "failed-inspections" [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "passed-inspections" [_ ctx]
  (inspection-or-violation-snippets-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "property-owners" [_ {:keys [permit-type]}]
  (permit-type-has-properties? permit-type))

(defmethod workspace-letter-variable-applicable? "applicants" [_ ctx]
  (applicants-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "other-parties" [_ ctx]
  (applicants-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "address" [_ {:keys [permit-type]}]
  (permit-type-has-properties? permit-type))

(defn- unit-placeholder-applicable? [{:keys [permit-type feature-flags]}]
  (and (permit-type-has-properties? permit-type)
       (contains? feature-flags :feature-flag.id/property-db-units)))

(defmethod workspace-letter-variable-applicable? "street-name"          [_ ctx] (unit-placeholder-applicable? ctx))
(defmethod workspace-letter-variable-applicable? "address-number"       [_ ctx] (unit-placeholder-applicable? ctx))
(defmethod workspace-letter-variable-applicable? "unit-type"            [_ ctx] (unit-placeholder-applicable? ctx))
(defmethod workspace-letter-variable-applicable? "unit-number"          [_ ctx] (unit-placeholder-applicable? ctx))
(defmethod workspace-letter-variable-applicable? "unit-type-and-number" [_ ctx] (unit-placeholder-applicable? ctx))
(defmethod workspace-letter-variable-applicable? "city"                 [_ ctx] (unit-placeholder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "property-id" [_ {:keys [permit-type]}]
  (permit-type-has-properties? permit-type))

(defmethod workspace-letter-variable-applicable? "deficiencies" [_ ctx]
  (deficiencies-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "compliant-deficiencies" [_ ctx]
  (deficiencies-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "non-compliant-deficiencies" [_ ctx]
  (deficiencies-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "violations" [_ ctx]
  (violations-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "compliant-violations" [_ ctx]
  (compliant-violations-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "non-compliant-violations" [_ ctx]
  (compliant-violations-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "bonds" [_ ctx]
  (bonds-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "conditions" [_ ctx]
  (conditions-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "issued-to" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "holder-street-address" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "holder-postal-code" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "holder-municipality" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "holder-mobile-phone" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "holder-state-or-province" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "date-issued" [_ {:keys [permit-type]}]
  (not= permit-type :permit-type/CE))

(defmethod workspace-letter-variable-applicable? "valid-from" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "valid-to" [_ ctx]
  (holder-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "case-description" [_ ctx]
  (case-and-legal-description-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "legal-description" [_ ctx]
  (case-and-legal-description-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "review-comments" [_ ctx]
  (reviews-applicable? ctx))

(defmethod workspace-letter-variable-applicable? "latest-review-comments" [_ ctx]
  (reviews-applicable? ctx))
